import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SezCertificateRequestItem } from "./SezActions";

export interface InvoiceDetailsData {
  linerLocation?: string;
  blNumber?: string;
  invoiceNo?: string;
  invoiceDate?: string;
  pendingSince?: string;
  amount?: string;
  consigneeName?: string;
  invoiceReleasedTo?: string;
  remarks?: string;
  requestId?: string;
  issueDate?: string;
  status?: string;
  payorId?: number | null;
  invoiceType?: string;
  sezInvNo?: string;
  sezInvDt?: string;
  sezInvAmt?: string;
  sezInvTp?: string;
}

export type SezInvoiceEditableField = "requestId" | "issueDate" | "remarks";


const ALLOWED_EDITABLE_FIELDS: SezInvoiceEditableField[] = [
  "requestId",
  "issueDate",
  "remarks",
];

interface SezInvoiceDetailsProps {
  invoiceForm?: InvoiceDetailsData | null;
  compact?: boolean;
  editableFields?: SezInvoiceEditableField[];
  onFieldChange?: (field: SezInvoiceEditableField, value: string) => void;
}

const fieldConfig: {
  key: keyof InvoiceDetailsData;
  label: string;
  type?: string;
  col: string;
}[] = [
  { key: "linerLocation", label: "Liner", col: "col-12 col-sm-6 col-lg-3" },
  { key: "blNumber", label: "BL Number", col: "col-12 col-sm-6 col-lg-3" },
  { key: "invoiceNo", label: "Invoice No", col: "col-12 col-sm-6 col-lg-3" },
  { key: "invoiceDate", label: "Invoice Date", col: "col-12 col-sm-6 col-lg-3" },

  { key: "pendingSince", label: "Pending Since", col: "col-12 col-sm-6 col-lg-3" },
  { key: "amount", label: "Amount", col: "col-12 col-sm-6 col-lg-3" },
  { key: "consigneeName", label: "Consignee Name", col: "col-12 col-sm-6 col-lg-3" },
  { key: "invoiceReleasedTo", label: "Invoice Released To", col: "col-12 col-sm-6 col-lg-3" },

  { key: "requestId", label: "Request ID", col: "col-12 col-md-4 col-lg-3" },
  { key: "issueDate", label: "Issue Date", type: "date", col: "col-12 col-md-4 col-lg-3" },
  { key: "remarks", label: "Remarks", col: "col-12 col-md-4 col-lg-6" },
];

const pick = (row: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }
  return "";
};

const formatPendingSinceDays = (value: unknown, status?: unknown) => {
  if (String(status ?? "").toUpperCase() === "APPROVED") {
    return "null";
  }

  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) return "0 days";

  const normalizedValue = trimmedValue.toLowerCase();
  if (normalizedValue.includes("day")) {
    return trimmedValue;
  }

  const numericValue = Number(trimmedValue);
  if (!Number.isNaN(numericValue)) {
    return `${numericValue} days`;
  }

  return trimmedValue;
};

export const extractSezBlAndInv = (row?: Record<string, any> | null) => {
  if (!row) {
    return { blNo: "", invNo: "" };
  }

  return {
    blNo: pick(row, ["bl_no", "blNo", "bl_number", "blNumber"]),
    invNo: pick(row, [
      "sez_inv_no",
      "inv_no",
      "invNo",
      "invoice_no",
      "invoiceNo",
    ]),
  };
};

export const mapSezRowToInvoiceDetails = (
  row?: Record<string, any> | null
): InvoiceDetailsData | null => {
  if (!row) return null;

  const liner = pick(row, ["liner_nm", "liner_name", "liner", "line"]);
  const location = pick(row, [
    "location_nm",
    "location",
    "liner_location",
    "countryCode",
    "country_code",
  ]);
  const amount = pick(row, [
    "sez_inv_amt",
    "inv_amt",
    "invAmt",
    "amount",
    "invamt",
  ]);
  const currency = pick(row, [
    "inv_currency",
    "invCurrency",
    "inv_curr",
    "currency",
  ]);

  return {
    linerLocation: [liner, location].filter(Boolean).join(" / ") || undefined,
    blNumber: pick(row, ["bl_no", "blNo", "blNumber", "bl_number"]) || undefined,
    invoiceNo:
      pick(row, ["sez_inv_no", "inv_no", "invNo", "invoice_no", "invoiceNo"]) ||
      undefined,
    invoiceDate:
      pick(row, [
        "sez_inv_dt",
        "inv_dt",
        "invDt",
        "invoice_date",
        "invoiceDate",
      ]) || undefined,

    pendingSince: formatPendingSinceDays(
      pick(row, ["pending_since", "pendingSince"]),
      pick(row, ["status", "Status"])
    ),
    amount: amount ? (currency ? `${amount} ${currency}` : amount) : undefined,
    consigneeName:
      pick(row, [
        "consignee_nm",
        "consigneeNm",
        "consignee_name",
        "consignee",
      ]) || undefined,
    invoiceReleasedTo:
      pick(row, ["invoice_released_to", "invoiceReleasedTo", "released_to"]) ||
      undefined,
    requestId: pick(row, ["sez_request_id", "sezRequestId"]) || "",
    issueDate:
      pick(row, [
        "sez_cert_issue_dt",
        "sezCertIssueDt",
        "issue_date",
        "issue_dt",
      ]) || "",
    remarks: pick(row, ["customer_remarks", "customerRemarks"]) || "",
    status: pick(row, ["status", "Status"]) || undefined,
  };
};

export const mapSezCertificateRequestToInvoiceDetails = (
  item?: SezCertificateRequestItem | null
): InvoiceDetailsData | null => {
  if (!item) return null;

  const amount =
    item.invAmt !== undefined && item.invAmt !== null
      ? `${item.invAmt}${item.invCurrency ? ` ${item.invCurrency}` : ""}`
      : "";

  return {
    linerLocation:
      [item.liner, item.countryCode].filter(Boolean).join(" / ") || "",
    blNumber: item.blNo || "",
    invoiceNo: item.invNo || "",
    invoiceDate: item.invDt || "",

    pendingSince: formatPendingSinceDays(item.pending_since, item.status),
    amount,
    consigneeName: item.consigneeNm || "",
    invoiceReleasedTo: item.invoiceReleasedTo || "",
    requestId:
      item.sezRequestId !== undefined && item.sezRequestId !== null
        ? String(item.sezRequestId)
        : "",
    issueDate: item.sezCertIssueDt || "",
    remarks: item.customerRemarks || "",
    status: item.status || "",
    payorId: item.payorId ?? null,
    invoiceType: item.invTp || item.sezInvTp || "",
    sezInvNo: item.sezInvNo || item.invNo || "",
    sezInvDt: item.sezInvDt || item.invDt || "",
    sezInvAmt:
      item.sezInvAmt !== undefined && item.sezInvAmt !== null
        ? String(item.sezInvAmt)
        : item.invAmt !== undefined && item.invAmt !== null
        ? String(item.invAmt)
        : "",
    sezInvTp: item.sezInvTp || item.invTp || "",
  };
};

const SezInvoiceDetails = ({
  invoiceForm,
  editableFields = [],
  onFieldChange,
}: SezInvoiceDetailsProps) => {
  const { t } = useTranslation();

  const statusAllowsEdit = ["PENDING", "SUBMITTED", "REJECTED"].includes(
    String(invoiceForm?.status || "").toUpperCase()
  );


  const editableSet = new Set<SezInvoiceEditableField>(
    statusAllowsEdit
      ? editableFields.filter((f) => ALLOWED_EDITABLE_FIELDS.includes(f))
      : []
  );

  return (
    <div className="w-100">
      <style>{`
        .sez-field-label {
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: #8a95a5;
        }
        .sez-field-control {
          border-radius: 6px;
          border-color: #dde1e6;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .sez-field-control:focus {
          border-color: #2c7be5;
          box-shadow: 0 0 0 0.15rem rgba(44, 123, 229, 0.15);
        }
        .sez-field-editable {
          background-color: #ffffff !important;
        }
        .sez-field-disabled {
          background-color: #f4f5f7 !important;
          color: #7c8697 !important;
          border-color: #e4e7eb !important;
          opacity: 1;
        }
        .sez-field-disabled:focus {
          box-shadow: none;
        }
        .sez-section-header {
          gap: 0.75rem;
        }
        @media (max-width: 575.98px) {
          .sez-section-header {
            align-items: flex-start !important;
          }
          .sez-status-badge {
            align-self: flex-start;
          }
        }
      `}</style>

      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 sez-section-header">
        <h3 className="fs--1 text-primary mb-0 fw-semibold">
          {t("resource_2:invoiceDetails") || "Invoice Details"}
        </h3>
        {invoiceForm?.status && (
          <span
            className="badge rounded-pill fs--2 px-3 py-1 sez-status-badge"
            style={{
              background:
                String(invoiceForm.status).toUpperCase() === "SUBMITTED"
                  ? "#e8f1ff"
                  : String(invoiceForm.status).toUpperCase() === "APPROVED"
                  ? "#ccf6e4"
                  : String(invoiceForm.status).toUpperCase() === "REJECTED"
                  ? "#fad7dd"
                  : String(invoiceForm.status).toUpperCase() === "PENDING"
                  ? "#d5e5fa"
                  : "#f1f3f5",
              color:
                String(invoiceForm.status).toUpperCase() === "SUBMITTED"
                  ? "#2c7be5"
                  : String(invoiceForm.status).toUpperCase() === "APPROVED"
                  ? "#00864e"
                  : String(invoiceForm.status).toUpperCase() === "REJECTED"
                  ? "#932338"
                  : String(invoiceForm.status).toUpperCase() === "PENDING"
                  ? "#1c4f93"
                  : "#6c757d",
            }}
          >
            {invoiceForm.status}
          </span>
        )}
      </div>

   
        <div className="row g-3">
          {fieldConfig.map(({ key, label, type, col }) => {
            const rawValue = invoiceForm?.[key];
            const value =
              key === "pendingSince" &&
              String(invoiceForm?.status || "").toUpperCase() === "APPROVED"
                ? "-"
                : rawValue === undefined || rawValue === null
                ? ""
                : String(rawValue);
            const isEditable = editableSet.has(key as SezInvoiceEditableField);
            const controlClass = `sez-field-control ${
              isEditable ? "sez-field-editable" : "sez-field-disabled"
            }`;

            return (
              <div className={`${col} mb-1`} key={key}>
                <Form.Group controlId={`sezInvoice.${key}`}>
                  <Form.Label className="sez-field-label mb-1 d-flex align-items-center">
                    {label}
                    {key === "requestId" || key === "issueDate" ? (
                      <span className="text-danger ms-1">*</span>
                    ) : null}
                  </Form.Label>
                  {key === "remarks" ? (
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      disabled={!isEditable}
                      readOnly={!isEditable}
                      value={value}
                      onChange={
                        isEditable
                          ? (e) => onFieldChange?.("remarks", e.target.value)
                          : undefined
                      }
                      className={controlClass}
                    />
                  ) : (
                    <Form.Control
                      size="sm"
                      type={isEditable && type === "date" ? "date" : "text"}
                      required={key === "requestId" || key === "issueDate"}
                      disabled={!isEditable}
                      readOnly={!isEditable}
                      value={value}
                      title={value}
                      onChange={
                        isEditable
                          ? (e) =>
                              onFieldChange?.(
                                key as SezInvoiceEditableField,
                                e.target.value
                              )
                          : undefined
                      }
                      className={controlClass}
                    />
                  )}
                </Form.Group>
              </div>
            );
          })}
        </div>
    </div>
  );
};

export default SezInvoiceDetails;
